import firebase from "firebase/app";
import "firebase/auth";
import { User, UserCustomClaims } from "../types";
import * as util from "@/util";
import { getServiceCall, postServiceCall } from "./base";
import config from "@/config";

export function reinforceSend(): Promise<void> {
  return postServiceCall("user/reinforce/send", {}) as Promise<void>;
}

export function reinforceVerify(code: string): Promise<void> {
  return postServiceCall("user/reinforce/verify", { code }) as Promise<void>;
}

export function onAuthStateChanged(handler: (user: User | null) => void) {
  firebase.auth().onAuthStateChanged((user) => {
    if (!user) {
      handler(null);
    } else {
      handler(wrapUser(user));
    }
  });
}

function isUIDToken(uid: string): boolean {
  return uid.startsWith("cpy-admin-");
}

export function wrapUser(user: firebase.User): User {
  return {
    uid: user.uid,
    isToken: isUIDToken(user.uid),
    name: isUIDToken(user.uid) ? "Administración" : user.displayName || user.email,
    email: user.email,
    fbUser: user,
    isGlobalAdmin: false,
    permissions: [],
    isLoading: true,
    claims: null,
    needsReinforced: false
  };
}

// Thrown by login() when the account has a phone second factor enrolled - carries
// the resolver needed (in mfa.ts) to challenge and complete sign-in with that factor.
export class MfaRequiredError extends Error {
  resolver: firebase.auth.MultiFactorResolver;

  constructor(resolver: firebase.auth.MultiFactorResolver) {
    super("Multi-factor authentication required");
    this.resolver = resolver;
  }
}

export async function login(email: string, password: string, rememberMe?: boolean): Promise<User> {
  await firebase
    .auth()
    .setPersistence(rememberMe ? firebase.auth.Auth.Persistence.LOCAL : firebase.auth.Auth.Persistence.SESSION);
  try {
    const credential = await firebase.auth().signInWithEmailAndPassword(email, password);
    if (credential.user) {
      return wrapUser(credential.user);
    }
    throw new Error("Login failed! No user assigned!");
  } catch (err) {
    if ((err as { code?: string }).code === "auth/multi-factor-auth-required") {
      throw new MfaRequiredError((err as firebase.auth.MultiFactorError).resolver);
    }
    throw err;
  }
}

export async function loginWithToken(authToken: string): Promise<User> {
  await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.SESSION);
  const credential = await firebase.auth().signInWithCustomToken(authToken);
  if (credential.user) {
    return wrapUser(credential.user);
  }
  throw new Error("Fallo el acceso con el token de autorización");
}

export async function refreshCurrentToken(): Promise<Partial<UserCustomClaims>> {
  const fbUser = firebase.auth().currentUser;
  if (fbUser == null) return {};
  const tokenResult = await fbUser.getIdTokenResult(true);
  const claims = (tokenResult.claims ?? {}) as Partial<UserCustomClaims>;
  if (claims.requiresReinforcedLogin) {
    // Downgrade persistence to session for users with sensitive access
    await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.SESSION);
  }
  return claims;
}

export async function logout(): Promise<void> {
  try {
    await getServiceCall("user/logout");
  } catch (err) {
    util.logError(`Error calling user/logout: ${(err as Error).message}`, err);
  }
  return firebase.auth().signOut();
}

export function resetPassword(email: string): Promise<void> {
  return firebase.auth().sendPasswordResetEmail(email, { url: config.rootWebURL + "authlogin" });
}

export async function changeUserData(userdata: { displayName: string }) {
  const user = firebase.auth().currentUser;
  if (user) {
    try {
      await user.updateProfile(userdata);
    } catch (err) {
      throw new Error(`Error al actualizar perfil: ${err}`, { cause: err as Error });
    }
  } else {
    throw new Error("Se ha perdido la sesión, recargue la página e inicie sesión nuevamente.");
  }
}

export async function checkEmail(email: string) {
  const signInMethods = await firebase.auth().fetchSignInMethodsForEmail(email);
  if (!util.isEmpty(signInMethods)) {
    return { exists: true };
  }
  return { exists: false };
}

export async function changePassword(prevPassword: string, newPassword: string): Promise<void> {
  let user = firebase.auth().currentUser;
  if (user && user.email) {
    const credential = firebase.auth.EmailAuthProvider.credential(user.email, prevPassword);
    try {
      await user.reauthenticateWithCredential(credential);
    } catch (err) {
      throw new Error("Error al verificar credenciales actuales, inicie sesión nuevamente", { cause: err as Error });
    }
    user = firebase.auth().currentUser;
    if (!user) {
      throw new Error("Se ha perdido la sesión, recargue la página e inicie sesión nuevamente.");
    }
    try {
      await user.updatePassword(newPassword);
    } catch (err) {
      throw new Error(`Error al cambiar la contraseña: ${err}`, { cause: err as Error });
    }
  } else {
    throw new Error("Se ha perdido la sesión, recargue la página e inicie sesión nuevamente.");
  }
}
